import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Server-derived discriminated union: `type` is authoritative from the server, never inferred
// here. For 'scored' entries `value` is a per-choice map keyed by answerId (a ScoreOption id or
// the `not_applicable` sentinel); it is `null` for single/multi.
interface SelectionEntry {
  type: string
  answerIds: string[]
  value: Record<string, string> | null
}

type SelectionsPayload = Record<string, SelectionEntry>

export default class extends Controller {
  static targets = ["buttonsContainer"]
  static values = {
    invitationKey: String
  }

  declare buttonsContainerTarget: HTMLElement
  declare invitationKeyValue: string

  async applySlate(event: Event): Promise<void> {
    event.preventDefault()

    const button = event.currentTarget as HTMLButtonElement
    const slateId = button.dataset.slateId

    if (!slateId) {
      console.error('Slate ID not found. Button dataset:', button.dataset)
      alert('Invalid group selection')
      return
    }

    try {
      // Disable all buttons and show loading state
      this.setLoading(button, true)
      this.disableAllButtons(true)

      const requestBody: { choice_group_id: string } = {
        choice_group_id: slateId
      }

      // Make AJAX request
      const response = await fetch(`/ballot/${this.invitationKeyValue}/slates/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken(),
          'Accept': 'text/vnd.turbo-stream.html'
        },
        body: JSON.stringify(requestBody)
      })
      
      // Read on both success and failure -- a failing apply (403/422) still carries a rendered
      // turbo-stream error; only a response with no stream body falls back to alert().
      const text = await response.text()

      if (!response.ok) {
        if (text.includes('<turbo-stream')) {
          Turbo.renderStreamMessage(text)
        } else {
          alert(`Error: ${response.status}`)
        }
        this.disableAllButtons(false)
        return
      }

      // Extract selections from custom turbo-stream action before rendering
      // Format: <turbo-stream action="slate_selections" target="..."><template>JSON</template></turbo-stream>
      const selectionsMatch = text.match(/<turbo-stream[^>]*action="slate_selections"[^>]*><template>([^<]*)<\/template><\/turbo-stream>/)
      let selections: SelectionsPayload | null = null
      
      if (selectionsMatch && selectionsMatch[1]) {
        try {
          selections = JSON.parse(selectionsMatch[1])
        } catch (e) {
          console.error('[Slate] Failed to parse selections JSON:', e)
        }
      }
      
      // Remove the custom action from the stream before rendering (Turbo doesn't know this action)
      const cleanedText = text.replace(/<turbo-stream[^>]*action="slate_selections"[^>]*><template>[^<]*<\/template><\/turbo-stream>/g, '')
      
      // Render remaining Turbo Stream content (if any)
      if (cleanedText.trim()) {
        Turbo.renderStreamMessage(cleanedText)
      }
      
      // Apply selections to form
      if (selections) {
        this.applySelectionsToForm(selections)
      }
      
    } catch (error) {
      console.error('Failed to apply slate:', error)
      alert(error instanceof Error ? error.message : 'Failed to apply group')
    } finally {
      this.setLoading(button, false)
      this.disableAllButtons(false)
    }
  }

  private applySelectionsToForm(selections: SelectionsPayload): void {
    const checkedInputs: HTMLInputElement[] = []

    // Clear all existing checkbox/radio selections first; a no-op for scored questions, whose
    // <select> elements are touched separately by applyScoredSelections below.
    Object.keys(selections).forEach(questionId => {
      const questionElement = document.getElementById(`question_${questionId}`)
      if (!questionElement) return

      // Uncheck all checkboxes and radio buttons in this question
      const inputs = questionElement.querySelectorAll<HTMLInputElement>(
        'input[type="checkbox"]:not([id*="abstain"]), input[type="radio"]:not([id*="abstain"])'
      )
      inputs.forEach(input => {
        input.checked = false
        // Remove selected class from parent row if it exists
        const row = input.closest('.question_row, tr')
        if (row) {
          row.classList.remove('selected')
        }
      })
    })

    // Apply new selections - collect all inputs first, then trigger events
    Object.entries(selections).forEach(([questionId, entry]) => {
      // Tolerate a null/malformed entry without throwing.
      if (!entry || typeof entry !== 'object') return

      switch (entry.type) {
        case 'single':
        case 'multi':
          this.applyChoiceSelections(questionId, entry.answerIds, checkedInputs)
          break
        case 'scored':
          this.applyScoredSelections(questionId, entry.answerIds, entry.value)
          break
        default:
          // `type` is server-derived; an unrecognized value fails closed rather than guessing a shape.
          break
      }
    })

    // Dispatch a single custom event after ALL selections are made
    // This avoids triggering per-input validations while still notifying
    // components like choice-group-instructions about the bulk selection
    const form = this.element.closest('form')
    if (form) {
      form.dispatchEvent(new CustomEvent('slate:selections-applied', {
        bubbles: true,
        detail: { inputCount: checkedInputs.length }
      }))
    }
  }

  private applyChoiceSelections(questionId: string, answerIds: string[], checkedInputs: HTMLInputElement[]): void {
    if (!Array.isArray(answerIds)) return

    answerIds.forEach(answerId => {
      const questionElement = document.getElementById(`question_${questionId}`)
      if (!questionElement) return

      // Checkbox ids follow Rails' ballot_data_questions_{q_id}_answers_{a_id} generation.
      let input = questionElement.querySelector<HTMLInputElement>(
        `input[type="checkbox"][id*="answers_${answerId}"]`
      )

      // Radios carry the answer id as their value, not in the id.
      if (!input) {
        input = questionElement.querySelector<HTMLInputElement>(
          `input[type="radio"][value="${answerId}"]`
        )
      }

      if (input) {
        input.checked = true

        const row = input.closest('.question_row, tr')
        if (row) {
          row.classList.add('selected')
        }

        checkedInputs.push(input)
      }
    })
  }

  // Each member answer's <select> gets its own echoed token (ScoreOption id or the
  // `not_applicable` sentinel) as-is -- two members of one question can carry different values.
  private applyScoredSelections(questionId: string, answerIds: string[], value: Record<string, string> | null): void {
    if (!Array.isArray(answerIds) || value == null) return

    answerIds.forEach(answerId => {
      if (!(answerId in value)) return

      const questionElement = document.getElementById(`question_${questionId}`)
      if (!questionElement) return

      const select = questionElement.querySelector<HTMLSelectElement>(
        `select[id*="answers_${answerId}"]`
      )
      if (select) {
        select.value = value[answerId]
        // Programmatic .value assignment fires no native 'change' event, so dispatch one to
        // keep live constraint indicators in sync.
        select.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })
  }

  private setLoading(button: HTMLButtonElement, loading: boolean): void {
    if (loading) {
      button.classList.add('ballots--slates-component__button--loading')
      button.disabled = true
    } else {
      button.classList.remove('ballots--slates-component__button--loading')
    }
  }

  private disableAllButtons(disabled: boolean): void {
    const buttons = this.buttonsContainerTarget.querySelectorAll('button')
    buttons.forEach(button => {
      button.disabled = disabled
    })
  }



  private csrfToken(): string {
    const meta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement
    return meta ? meta.content : ''
  }
}
